import React from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { LinkButton } from '@atlaskit/button/new';
import AddIcon from '@atlaskit/icon/core/add';
import { fg } from '@atlaskit/platform-feature-flags';
import { useRovoAgentCreateAnalytics } from '@atlaskit/rovo-agent-analytics/create';
import { useProfileController } from '@atlassian/conversation-assistant-store/controllers/profile';

import { getAtlassianStudioNLCreateUrl } from '../../../../common/utils/atlassian-studio-url/index.tsx';
import { useAgentSitePermissions } from '../../../../controllers/agent-site-permissions/index.tsx';

import { BaseEmptyState, illustrationStyle } from './base-empty-state.tsx';
import messages from './messages.ts';
import { NoCreatedAgentsIllustration } from './no-created.illustration.tsx';

export type NoCreatedAgentsEmptyStateProps = {
	siteId: string;
	navigationSource: string;
	singleColumn?: boolean;
};

export const NoCreatedAgentsEmptyState = ({
	siteId,
	navigationSource,
	singleColumn,
}: NoCreatedAgentsEmptyStateProps): React.JSX.Element => {
	di(useAgentSitePermissions, useProfileController, useRovoAgentCreateAnalytics);
	const { formatMessage } = useIntl();
	const { email } = useProfileController();
	const [rovoAgentCSID, { trackCreateSessionStart }] = useRovoAgentCreateAnalytics({
		touchPoint: navigationSource,
	});
	const { data: sitePermissions } = useAgentSitePermissions({
		touchpointSource: 'browseAgentsContent',
	});
	const isAbleToCreateAgents =
		sitePermissions?.data?.atlassianStudio_userSiteContext?.userPermissions?.isAbleToCreateAgents ??
		false;

	const illustration = (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values
		<NoCreatedAgentsIllustration style={illustrationStyle} />
	);

	if (!isAbleToCreateAgents) {
		return (
			<BaseEmptyState
				singleColumn={singleColumn}
				illustration={illustration}
				heading={formatMessage(messages.noCreatedAgentsHeaderNoPermission)}
				description={formatMessage(messages.noCreatedAgentsNoPermission)}
			/>
		);
	}

	return (
		<BaseEmptyState
			singleColumn={singleColumn}
			illustration={illustration}
			heading={formatMessage(messages.noCreatedAgentsHeader)}
			description={formatMessage(messages.noCreatedAgents)}
			cta={
				<LinkButton
					appearance="primary"
					href={getAtlassianStudioNLCreateUrl(siteId, navigationSource, rovoAgentCSID, email)}
					target="_blank"
					iconBefore={AddIcon}
					onClick={() => {
						fg('rovo-agents-universal-analytics') && trackCreateSessionStart();
					}}
					onAuxClick={() => {
						fg('rovo-agents-universal-analytics') && trackCreateSessionStart();
					}}
				>
					{formatMessage(messages.createAnAgentButton)}
				</LinkButton>
			}
		/>
	);
};
